import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { PageHeader, Card, StatCard, Badge, Spinner, QueryError } from "../components";
import { apiGet } from "../lib/api";

interface LaunchReadinessCheck {
  id: string;
  label: string;
  ready: boolean;
  detail: string;
}

interface LaunchReadinessResponse {
  status: "ready" | "blocked";
  evaluatedAt: string;
  blockers: string[];
  checks: LaunchReadinessCheck[];
  truth: {
    manifestSource: string;
    nodeEnv: string;
    backup: {
      provider: string;
      bucket: boolean;
      storageAccount: boolean;
    };
    lastRestoreDrill: string | null;
  } | null;
}

function formatTime(value: string | null | undefined): string {
  if (!value) return "missing";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
}

function checkColor(ready: boolean): "green" | "red" {
  return ready ? "green" : "red";
}

export default function LaunchReadiness() {
  const { data, isLoading, error } = useQuery<LaunchReadinessResponse>({
    queryKey: ["launch-readiness"],
    queryFn: () => apiGet("/api/launch/readiness"),
    refetchInterval: 30_000,
  });

  const readyCount = useMemo(
    () => data?.checks.filter((check) => check.ready).length ?? 0,
    [data?.checks],
  );
  const blockedCount = data?.blockers.length ?? 0;
  const totalCount = data?.checks.length ?? 0;

  if (isLoading && !data) {
    return (
      <div>
        <PageHeader title="Launch Readiness" subtitle="Canonical production truth" />
        <div className="flex items-center justify-center py-20">
          <Spinner size={24} />
        </div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div>
        <PageHeader title="Launch Readiness" subtitle="Canonical production truth" />
        <QueryError error={error} queryKeys={[["launch-readiness"]]} />
      </div>
    );
  }

  return (
    <div>
      <PageHeader title="Launch Readiness" subtitle="Canonical production truth" />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <StatCard
          title="Launch State"
          value={data?.status === "ready" ? "Ready" : "Blocked"}
          color={data?.status === "ready" ? "var(--c-success)" : "var(--c-danger)"}
        />
        <StatCard title="Checks Passing" value={`${readyCount}/${totalCount}`} />
        <StatCard
          title="Blockers"
          value={String(blockedCount)}
          color={blockedCount ? "var(--c-danger)" : "var(--c-success)"}
        />
      </div>

      <Card className="mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="text-sm font-semibold" style={{ color: "var(--c-text)" }}>
              Runtime identity
            </p>
            <p className="text-xs mt-1" style={{ color: "var(--c-text-secondary)" }}>
              {data?.truth?.manifestSource ?? "unknown"} manifest,{" "}
              {data?.truth?.nodeEnv ?? "unknown"} node env
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Badge color={data?.status === "ready" ? "green" : "red"} dot>
              {data?.status ?? "blocked"}
            </Badge>
            <Badge color="gray">evaluated {formatTime(data?.evaluatedAt)}</Badge>
            <Badge color="gray">restore drill {formatTime(data?.truth?.lastRestoreDrill)}</Badge>
          </div>
        </div>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {(data?.checks ?? []).map((check) => (
          <Card key={check.id} className="min-h-[118px]">
            <div className="flex items-start justify-between gap-4">
              <div>
                <p className="text-sm font-semibold" style={{ color: "var(--c-text)" }}>
                  {check.label}
                </p>
                <p className="text-sm mt-2" style={{ color: "var(--c-text-secondary)" }}>
                  {check.detail}
                </p>
              </div>
              <Badge color={checkColor(check.ready)} dot>
                {check.ready ? "ready" : "blocked"}
              </Badge>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
